import { Spacer, Typography } from '@qbe/ads-core';
import React from 'react';

import List from '~/components-ds/List';
import Modal from '~/components-ds/Modal';
import { useAppContent } from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import type { Cover } from '~/types/cover';
import { parseJSX } from '~/utils/sitecore';

type CoverInclusionsModalProps = {
  inclusionId: Cover;
  open: boolean;
  onClose: () => unknown;
  analyticsModalName: string;
};

export const CoverInclusionsModal: React.FC<CoverInclusionsModalProps> = ({
  inclusionId,
  open,
  onClose,
  analyticsModalName,
}) => {
  const { selectUiCover } = useAppContent();
  const inclusion = selectUiCover(inclusionId);
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverOptionComponent',

    state: {
      policyWordingLink: getSharedContent('policyWordingLink'),
    },
    tokens: {
      inclusionId: () => inclusionId,
      policyWordingLink: (state) => state.policyWordingLink,
    },
  });

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  const listIncluded = Object.values(inclusion?.listIncluded || {});
  const notIncluded = Object.values(inclusion?.listNotIncluded || {});

  return (
    <Modal
      id="cover-inclusions-modal"
      open={open}
      primaryBtnProps={{
        label: getSharedContent('closeModalButtonLabel'),
        onClick: onClose,
      }}
      title={getTextContent('content.coverInclusionsModal.title')}
      analyticsModalName={analyticsModalName}
    >
      <>
        <Typography>{inclusion.inclusionDescription ?? inclusion.description}</Typography>
        <Spacer size="lg" />

        {listIncluded.length > 0 ? (
          <>
            <Typography variant="h5" gutterBottom>
              {getTextContent('content.inclusionHeading.title')}
            </Typography>
            <List
              variant="validation"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              list={listIncluded.map((i: any, index: number) => ({
                id: index,
                item: parseJSX(i),
              }))}
            />
            <Spacer size="md" />
          </>
        ) : null}

        {notIncluded.length > 0 ? (
          <>
            <Typography variant="h5" gutterBottom>
              {getTextContent('content.exclusionHeading.title')}
            </Typography>
            <List
              variant="exclusion"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              list={notIncluded.map((i: any, index: number) => ({
                id: index,
                item: parseJSX(i),
              }))}
            />
            <Spacer size="md" />
          </>
        ) : null}

        <Typography variant="body2" textAlign="left">
          {getJSXContent('content.policyDetails.description')}
        </Typography>
      </>
    </Modal>
  );
};
